import React, { useEffect, useState } from 'react';
import { Card, Row, Col, Button, Badge, Modal, Form, Alert } from 'react-bootstrap';
import { showTemplates, syncTemplates, getCompany, deleteTemplate } from '../../../helpers';
import './TemplateListPage.css';
import ViewTemplate from './ViewTemplate';
import { Eye, Trash, ArrowRepeat } from 'react-bootstrap-icons';

interface Template {
    template_id: number;
    template_name: string;
    category: string;
    language: string;
    wa_approval_status: string;
    status: string;
    created_at: string;
    title?: string;
    title_text?: string;
    title_type?: string;
    title_file?: string;
    body: string;
    footer?: string;
    buttons?: any[];
    header_variables?: string[];
    variables?: string[];
}

const ITEMS_PER_PAGE = 6;
interface Props {
    alertMsg?: string | null;
    alertVariant?: 'success' | 'danger' | 'primary';
}

const ListPage: React.FC<Props> = ({ alertMsg, alertVariant = 'success' }) => {
    const [templates, setTemplates] = useState<Template[]>([]);
    const [filteredTemplates, setFilteredTemplates] = useState<Template[]>([]);
    const [loading, setLoading] = useState(true);
    const [syncLoading, setSyncLoading] = useState(false);
    const [companyName, setCompanyName] = useState('Loading...');
    const [profile, setProfile] = useState('logo');
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
    const [activeCategory, setActiveCategory] = useState('All');
    const [categoryCounts, setCategoryCounts] = useState<{ [k: string]: number }>({});
    const [searchText, setSearchText] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [company, setCompanyid] = useState('0');

    const companyId =
        JSON.parse(sessionStorage.getItem('shreyu_user') || '{}')?.user?.company_id;

    const normalizeTemplates = (list: Template[]) =>
        list.map(t => ({ ...t, buttons: Array.isArray(t.buttons) ? t.buttons : [] }));

    const loadTemplates = async () => {
        const res = await showTemplates();
        const data = normalizeTemplates(res.data.templates);

        setTemplates(data);
        setFilteredTemplates(data);

        const counts = { All: data.length, Marketing: 0, Utility: 0, Authentication: 0 };

        data.forEach(t => {
            const c = t.category?.toLowerCase();
            if (c === 'marketing') counts.Marketing++;
            if (c === 'utility') counts.Utility++;
            if (c === 'authentication') counts.Authentication++;
        });

        setCategoryCounts(counts);
        setLoading(false);
    };

    useEffect(() => {
        loadTemplates();
    }, []);

    useEffect(() => {
        (async () => {
            try {
                const res = await getCompany(companyId);
                setCompanyName(res.data.company_name);
                setCompanyid(companyId);
                setProfile(res.data.logo || 'logo');
            } catch { }
        })();
    }, []);

    const handleSync = async () => {
        setSyncLoading(true);
        try {
            await syncTemplates(companyId);
            await loadTemplates();
        } finally {
            setSyncLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        if (!window.confirm('Are you sure you want to delete this template?')) return;

        try {
            const res = await deleteTemplate(id);
            if (res.data.success) {
                setTemplates(prev => prev.filter(t => t.template_id !== id));
                setFilteredTemplates(prev => prev.filter(t => t.template_id !== id));
            }
        } catch (err) {
            console.error(err);
        }
    };

    const filterByCategory = (cat: string) => {
        setActiveCategory(cat);
        setCurrentPage(1);

        if (cat === 'All') setFilteredTemplates(templates);
        else
            setFilteredTemplates(
                templates.filter(t => t.category?.toLowerCase() === cat.toLowerCase())
            );
    };

    const getStatusBadge = (status: string) => {
        switch (status?.toLowerCase()) {
            case 'approved':
                return <Badge bg="success">Approved</Badge>;
            case 'pending':
                return <Badge bg="warning">Pending</Badge>;
            case 'rejected':
                return <Badge bg="danger">Rejected</Badge>;
            default:
                return <Badge bg="secondary">{status || 'Unknown'}</Badge>;
        }
    };

    const escapeRegExp = (string: string) =>
        string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    const renderPreviewText = (text: string, variables: string[] = []) => {
        let result = text || '';

        variables.forEach((v, i) => {
            const regex = new RegExp(escapeRegExp(v), 'g');
            result = result.replace(regex, `[Variable ${i + 1}]`);
        });

        result = result
            .replace(/\*(.*?)\*/g, '<strong>$1</strong>')
            .replace(/_(.*?)_/g, '<em>$1</em>')
            .replace(/~(.*?)~/g, '<span style="text-decoration: line-through">$1</span>')
            .replace(/\\n/g, '<br/>')
            .replace(/\n/g, '<br/>');

        return result;
    };

    const renderButtonPreview = (button: any, index: number) => {
        if (!button) return null;
        const { types, text } = button;

        return (
            <div key={index} className="wa-button-wrapper">
                {(types === 'PHONE_NUMBER' || types === 'call_to_action') && (
                    <b style={{ color: '#1E90FF' }}>{text || 'Call'}</b>
                )}
                {types === 'URL' && <b style={{ color: '#1E90FF' }}>{text || 'Visit Site'}</b>}
                {types === 'QUICK_REPLY' && (
                    <b style={{ color: '#1E90FF' }}>{text || 'Quick Reply'}</b>
                )}
            </div>
        );
    };

    const renderHeaderPreview = (template: Template) => {
        if (!template.title_type || template.title_type === 'none') return null;

        if (template.title_type.toLowerCase() === 'text') {
            return (
                <div
                    dangerouslySetInnerHTML={{
                        __html: renderPreviewText(template.title || '', template.header_variables || [])
                    }}
                />
            );
        }

        if (template.title_type.toLowerCase() === 'image') {
            return (
                <img
                    src={`https://api.skynyx.ai/file_storage/${companyId}/template/${template.title_file}`}
                    alt=""
                    style={{ width: '50%', borderRadius: 6 }}
                />
            );
        }

        return null;
    };

    const searchedTemplates = filteredTemplates.filter(t =>
        t.template_name?.toLowerCase().includes(searchText.toLowerCase())
    );

    const totalPages = Math.ceil(searchedTemplates.length / ITEMS_PER_PAGE);

    const paginatedTemplates = searchedTemplates.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    if (loading) return <div className="text-center py-5">Loading templates...</div>;

    return (
        // <>
        <Row >
            {alertMsg && (
                <Alert variant={alertVariant} dismissible>
                    {alertMsg}
                </Alert>
            )}

            <Col md={2}>
                <Card>
                    <Card.Body>
                        <h6>Groups</h6>

                        {['All', 'Marketing', 'Utility', 'Authentication'].map((cat) => (
                            <div
                                key={cat}
                                className={`group-item ${activeCategory === cat ? 'active' : ''}`}
                                onClick={() => filterByCategory(cat)}
                            >
                                {cat} ({categoryCounts[cat] || 0})
                            </div>
                        ))}


                    </Card.Body>
                </Card>
            </Col>

            <Col md={10}>
                <Card>
                    <Card.Body>
                        <Row>
                            <Col>
                                <div className="template-toolbar">
                                    <Form.Control
                                        size="sm"
                                        placeholder="Search template..."
                                        value={searchText}
                                        onChange={e => {
                                            setSearchText(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    />
                                </div>
                            </Col>
                            <Col>
                                <Button size="sm" onClick={handleSync} disabled={syncLoading}>
                                    <ArrowRepeat className={syncLoading ? 'spin me-1' : 'me-1'} />
                                    Sync
                                </Button>
                            </Col>
                        </Row>

                        <Row className="mt-3 g-3">
                            {paginatedTemplates.map(template => (
                                <Col key={template.template_id} lg={6} xl={4}>
                                    <Card className=" template-card">
                                        <Card.Header className=" text-white d-flex justify-content-between align-items-center py-1 px-2" style={{
                                background: 'linear-gradient(135deg, #0eb7eb, #167491)',
                               
                            }}>
                                            <div style={{ fontSize: '12px', lineHeight: '1.2' }}>
                                                <div style={{ fontWeight: 600 }}>{template.template_name}</div>
                                                <div className="text-light">
                                                    {template.category} • {template.language}
                                                </div>
                                            </div>

                                            <div style={{ fontSize: '11px' }}>
                                                {getStatusBadge(template.wa_approval_status)}
                                            </div>
                                        </Card.Header>

                                        <Card.Body>
                                            <div className="template-preview-container">
                                                <div className="template-preview fixed-preview">
                                                    <div className="template-header-preview">
                                                        <div className="template-profile-pic">
                                                            <img
                                                                src={`https://api.skynyx.ai/storage/app/public/${profile}`}
                                                                className="me-2 rounded-circle"
                                                                height="40"
                                                                alt=""
                                                                style={{ backgroundColor: "white" }}
                                                            />
                                                        </div>
                                                        <div className="template-contact">
                                                            <div className="template-contact-name text-white">{companyName}</div>
                                                            <div className="template-contact-status">online</div>
                                                        </div>
                                                    </div>
                                                    <div className="template-message-preview">
                                                        {renderHeaderPreview(template)}
                                                        <div className="template-body-preview pl-2" style={{ paddingTop: "6px", paddingBottom: "6px", borderRadius: "6px", paddingLeft: "11px", paddingRight: "11px" }}  >
                                                            <div dangerouslySetInnerHTML={{
                                                                __html: renderPreviewText(template.body, template.variables || [])
                                                            }} />
                                                        </div>
                                                        {template.footer && (
                                                            <div className="template-footer-preview" style={{ paddingTop: "6px", paddingBottom: "6px", borderRadius: "6px", paddingLeft: "11px", paddingRight: "11px" }}>
                                                                {template.footer}
                                                            </div>
                                                        )}
                                                        {/* {console.log(template.buttons)} */}
                                                        {template.buttons && template.buttons.length > 0 && (
                                                            <div className="template-buttons-preview" style={{ paddingTop: "6px", paddingBottom: "6px", borderRadius: "6px", paddingLeft: "11px", paddingRight: "11px" }}>
                                                                {template.buttons.map((button, index) =>
                                                                    renderButtonPreview(button, index)
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </Card.Body>

                                        <Card.Footer className="text-center">
                                            <Row>
                                                <Col>
                                                    <small>
                                                        {new Date(template.created_at).toLocaleDateString()}
                                                    </small>
                                                </Col>
                                                <Col>
                                                    <div className=" d-flex justify-content-center gap-2">
                                                        <Button
                                                            size="sm"
                                                            variant="info"
                                                            onClick={() => {
                                                                setSelectedTemplateId(template.template_id);
                                                                setShowTemplateModal(true);
                                                            }}
                                                        >
                                                            <Eye />
                                                        </Button>

                                                        <Button
                                                            size="sm"
                                                            variant="danger"
                                                            onClick={() => handleDelete(template.template_id)}
                                                        >
                                                            <Trash />
                                                        </Button>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </Card.Footer>
                                    </Card>
                                </Col>
                            ))}
                        </Row>

                        {totalPages > 1 && (
                            <div className="d-flex justify-content-center align-items-center gap-3 mt-3">
                                <Button size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
                                    Prev
                                </Button>
                                <span>
                                    Page {currentPage} / {totalPages}
                                </span>
                                <Button size="sm" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                                    Next
                                </Button>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            </Col>


            <Modal show={showTemplateModal} onHide={() => setShowTemplateModal(false)} size="xl">
                <Modal.Header closeButton />
                <Modal.Body>
                    <ViewTemplate templateId={selectedTemplateId} />
                </Modal.Body>
            </Modal>
        </Row>

        // </>
    );
};

export default ListPage;
